import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import SEO from '@/components/SEO';
import { AlertTriangle, Printer, FileText, Ruler, ExternalLink } from 'lucide-react';
import { COMPANY, TERMS_VERSION, TERMS_LAST_UPDATED } from '@/config/site';

/**
 * Terms & Conditions — England-and-Wales draft.
 * This page is intentionally verbose because it is a legal document.
 * Keep the language plain, avoid fear-based framing, and never claim it has
 * been legally reviewed. Section wording follows the client brief and must be
 * checked by a UK solicitor before public launch of paid Plan Measure.
 */

const toc = [
  { id: 'section-1', label: '1. Definitions' },
  { id: 'section-2', label: '2. Two distinct services' },
  { id: 'section-3', label: '3. Managed Plan Service' },
  { id: 'section-4', label: '4. Plan Measure online tool' },
  { id: 'section-5', label: '5. Source Material and plan rights' },
  { id: 'section-6', label: '6. Intellectual property' },
  { id: 'section-7', label: '7. Pricing, payment and VAT' },
  { id: 'section-8', label: '8. Consumer cancellation and refunds' },
  { id: 'section-9', label: '9. Accuracy, verification and output disclaimers' },
  { id: 'section-10', label: '10. Liability' },
  { id: 'section-11', label: '11. Data protection and privacy' },
  { id: 'section-12', label: '12. Complaints, support and copyright reports' },
  { id: 'section-13', label: '13. Suspension and termination' },
  { id: 'section-14', label: '14. Changes to these Terms' },
  { id: 'section-15', label: '15. General' },
  { id: 'section-16', label: '16. Governing law' },
  { id: 'section-17', label: '17. Contact details' },
];

const H2 = ({ id, children }) => (
  <h2 id={id} className="scroll-mt-24 text-2xl font-bold text-gray-900 mt-10 mb-4">{children}</h2>
);

const H3 = ({ children }) => (
  <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-2">{children}</h3>
);

const P = ({ children, className = '' }) => (
  <p className={`text-gray-700 leading-relaxed my-3 ${className}`}>{children}</p>
);

const UL = ({ children }) => (
  <ul className="list-disc pl-6 my-3 space-y-1.5 text-gray-700">{children}</ul>
);

const TermsAndConditions = () => {
  useEffect(() => {
    // Ensure hash links jump correctly after render
    if (window.location.hash) {
      const el = document.getElementById(window.location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 200);
    }
    // Set a self-referencing canonical tag. Helmet-based canonicals across the
    // site have a known regression we handle site-wide separately; for this
    // legal page we set it explicitly so search engines always see the right URL.
    const CANONICAL = 'https://houseplandirect.com/legal/terms';
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    const previous = link.getAttribute('href');
    link.setAttribute('href', CANONICAL);
    return () => {
      // Reset canonical on unmount so other routes aren't affected.
      if (previous) link.setAttribute('href', previous);
    };
    // Deliberate one-shot mount-only effect. CANONICAL is a local const,
    // `el` and `link` are captured DOM refs — none warrant re-running.
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        pageId="terms"
        defaultTitle="Terms & Conditions | House Plan Direct"
        defaultDescription="Terms and Conditions for the House Plan Direct managed floor-plan service and the Plan Measure online tool. England-and-Wales draft — for solicitor review."
      />

      {/* Print-friendly styles scoped to this page */}
      <style>{`
        @media print {
          header, footer, .no-print { display: none !important; }
          a[href]::after { content: " (" attr(href) ")"; font-size: 0.85em; color: #666; }
          body { color: #000; }
          .print-shell { max-width: 100% !important; padding: 0 !important; }
        }
      `}</style>

      <div className="no-print"><Header /></div>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 print-shell">
        {/* Title + last-updated + print */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900" data-testid="terms-title">Terms &amp; Conditions</h1>
            <p className="text-sm text-gray-500 mt-2" data-testid="terms-last-updated">
              Last updated: <strong>{TERMS_LAST_UPDATED}</strong> · Version: <span className="font-mono">{TERMS_VERSION}</span>
            </p>
          </div>
          <button
            onClick={() => window.print()}
            className="no-print flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            data-testid="terms-print"
            aria-label="Print this page"
          >
            <Printer className="h-4 w-4" /> Print
          </button>
        </div>

        {/* Solicitor-review banner — not "legally reviewed" */}
        <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 flex gap-3" data-testid="terms-draft-notice">
          <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <div>
            <strong className="block">Working draft — not legally reviewed.</strong>
            This page is a professional draft for England and Wales. It must be reviewed by a UK commercial and consumer-law solicitor before the paid self-service Plan Measure tool launches.
          </div>
        </div>

        {/* Legal entity */}
        <div className="mt-6 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-800" data-testid="terms-legal-entity">
          <p>
            House Plan Direct is a trading name of <strong>{COMPANY.legalName}</strong>, a company registered in {COMPANY.registeredIn} under
            company number <strong>{COMPANY.companyNumber}</strong>. Registered office: {COMPANY.registeredOffice}.
          </p>
        </div>

        {/* Introduction */}
        <P className="mt-6">
          These Terms &amp; Conditions apply to the professional floor-plan conversion service provided by House Plan Direct and to
          the Plan Measure online measurement and drawing tool. They explain the basis on which we provide our services and software.
        </P>
        <P>
          By placing an order, creating an account, subscribing, uploading material, using Plan Measure or downloading an output,
          you agree to these Terms.
        </P>

        {/* Two-service summary box */}
        <div className="mt-6 grid sm:grid-cols-2 gap-4" data-testid="terms-service-summary">
          <div className="rounded-lg border border-blue-100 bg-blue-50/60 p-5">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="h-4 w-4 text-blue-700" />
              <h3 className="font-semibold text-blue-900">Managed Plan Service</h3>
            </div>
            <p className="text-sm text-blue-900/90">
              House Plan Direct converts authorised customer-supplied sketches, measurements and source information into
              finished property-plan files. Formats: JPEG, PDF, DWG.
            </p>
          </div>
          <div className="rounded-lg border border-emerald-100 bg-emerald-50/60 p-5">
            <div className="flex items-center gap-2 mb-2">
              <Ruler className="h-4 w-4 text-emerald-700" />
              <h3 className="font-semibold text-emerald-900">Plan Measure</h3>
            </div>
            <p className="text-sm text-emerald-900/90">
              A self-service online tool for working with your own authorised plans, sketches and measurements.
              Export formats: PNG, PDF, DXF only.
            </p>
          </div>
        </div>

        {/* Related legal links */}
        <div className="mt-6 flex flex-wrap gap-x-4 gap-y-2 text-sm no-print">
          <Link to="/legal/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link>
          <Link to="/legal/refunds-and-cancellation" className="text-blue-600 hover:underline">Refunds &amp; Cancellation</Link>
          <Link to="/legal/copyright-and-takedown" className="text-blue-600 hover:underline">Copyright &amp; Takedown</Link>
          <Link to="/legal/complaints" className="text-blue-600 hover:underline">Complaints Procedure</Link>
        </div>

        {/* Table of contents */}
        <nav className="mt-8 rounded-lg border border-gray-200 bg-white p-5" aria-label="Contents" data-testid="terms-toc">
          <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3">Contents</h2>
          <ol className="grid sm:grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
            {toc.map((t) => (
              <li key={t.id}>
                <a href={`#${t.id}`} className="text-blue-600 hover:underline">{t.label}</a>
              </li>
            ))}
          </ol>
        </nav>

        {/* ---- Section 1 ---- */}
        <H2 id="section-1">1. Definitions</H2>
        <UL>
          <li><strong>Business Customer</strong> — a person acting wholly or mainly for purposes relating to their trade, business, craft or profession.</li>
          <li><strong>Consumer</strong> — an individual acting wholly or mainly outside their trade, business, craft or profession.</li>
          <li><strong>Customer</strong> — any person or organisation using the Services or Plan Measure.</li>
          <li><strong>Managed Plan Service</strong> — the professional service where House Plan Direct converts customer-supplied information into finished digital plans.</li>
          <li><strong>Plan Measure</strong> — the online self-service measurement, tracing, drawing, annotation and export tool.</li>
          <li><strong>Source Material</strong> — sketches, measurements, photographs, plans, drawings, annotations, files and other material submitted or uploaded by the Customer.</li>
          <li><strong>Managed-Service Deliverables</strong> — the completed files produced by House Plan Direct through the Managed Plan Service.</li>
          <li><strong>Plan Measure Outputs</strong> — files generated and exported by the Customer through Plan Measure.</li>
          <li><strong>Working Day</strong> — Monday to Friday, excluding public holidays in England.</li>
        </UL>

        {/* ---- Section 2 ---- */}
        <H2 id="section-2">2. Two distinct services</H2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="rounded-lg border border-gray-200 p-5 bg-white">
            <h3 className="font-semibold text-gray-900 mb-2">Managed Plan Service</h3>
            <p className="text-sm text-gray-700">
              House Plan Direct converts authorised customer-supplied sketches, measurements and source information into finished property-plan files.
            </p>
            <ul className="mt-3 list-disc pl-5 space-y-1 text-sm text-gray-700">
              <li>This is a managed professional conversion service.</li>
              <li>House Plan Direct carries out the conversion and drawing work.</li>
              <li>Final Managed-Service Deliverables may be supplied in JPEG, PDF and DWG formats, subject to the order specification.</li>
            </ul>
          </div>
          <div className="rounded-lg border border-gray-200 p-5 bg-white">
            <h3 className="font-semibold text-gray-900 mb-2">Plan Measure</h3>
            <p className="text-sm text-gray-700">
              Plan Measure is a self-service online tool that allows customers to work with their own authorised plans, sketches and measurements.
            </p>
            <ul className="mt-3 list-disc pl-5 space-y-1 text-sm text-gray-700">
              <li>The Customer uses the tool directly.</li>
              <li>Plan Measure export formats are <strong>PNG, PDF and DXF only</strong>. Plan Measure does not provide JPEG or DWG exports.</li>
              <li>Plan Measure does not include a manual redraw service unless the Customer separately orders the Managed Plan Service.</li>
              <li>Tool features, exports, file limits and availability may change as the product develops.</li>
            </ul>
          </div>
        </div>

        {/* ---- Section 3 ---- */}
        <H2 id="section-3">3. Managed Plan Service</H2>
        <H3>3.1 Scope</H3>
        <P>House Plan Direct may convert customer-supplied sketches, measurements and authorised source material into professional digital floor plans, elevations, sections, layout drawings and related visual outputs.</P>
        <P>Optional extras may include colour, furniture, symbols, keys and other agreed features.</P>

        <H3>3.2 Intended use</H3>
        <UL>
          <li>property marketing;</li>
          <li>sales and lettings;</li>
          <li>layout understanding;</li>
          <li>interior-design discussions;</li>
          <li>preliminary property records;</li>
          <li>general non-regulatory planning discussions.</li>
        </UL>

        <H3>3.3 What the Managed Plan Service is not</H3>
        <P>Unless expressly agreed in writing and supported by an appropriate professional appointment, Managed-Service Deliverables are not:</P>
        <UL>
          <li>measured building surveys;</li>
          <li>legal boundary plans;</li>
          <li>title plans;</li>
          <li>valuation plans;</li>
          <li>architectural plans for construction;</li>
          <li>structural-engineering drawings;</li>
          <li>planning-application drawings;</li>
          <li>Building Regulations drawings;</li>
          <li>fire-safety drawings;</li>
          <li>EPC certificates;</li>
          <li>accreditation-scheme records;</li>
          <li>RICS-compliant measurements;</li>
          <li>survey-grade or legally accurate drawings.</li>
        </UL>

        <H3>3.4 Customer information</H3>
        <P>The Customer must provide accurate information, measurements and instructions to the best of their knowledge.</P>
        <P>The Customer remains responsible for checking final room names, dimensions, layout, orientation, notes and property information before relying on or distributing a completed plan.</P>

        <H3>3.5 Corrections</H3>
        <P>House Plan Direct will correct genuine conversion errors caused by House Plan Direct without additional charge where notified within 7 days of delivery.</P>
        <P>Changes arising from amended customer instructions, changed property information, inaccurate source material, new measurements or changed design preferences may be charged separately.</P>

        <H3>3.6 Delivery</H3>
        <P>Delivery times must be presented as estimates unless a specific written deadline is agreed.</P>
        <P>Managed-Service Deliverables are normally supplied electronically in the agreed JPEG, PDF and DWG formats.</P>

        {/* ---- Section 4 ---- */}
        <H2 id="section-4">4. Plan Measure online tool</H2>

        <H3>4.1 Account responsibility</H3>
        <P>Customers must keep account passwords and access credentials secure and must not allow unauthorised people to use their account.</P>
        <P>Customers are responsible for activity carried out through their account unless they notify House Plan Direct promptly of suspected unauthorised access.</P>

        <H3>4.2 Permitted use</H3>
        <P>Plan Measure may be used only for lawful property-plan, tracing, measurement, annotation and drawing work relating to material the Customer is authorised to use.</P>
        <P>Customers must not:</P>
        <UL>
          <li>reverse engineer, copy, scrape, interfere with or attempt to bypass the tool;</li>
          <li>use the tool to develop a competing product;</li>
          <li>upload malicious code or files;</li>
          <li>use the tool to infringe intellectual-property rights;</li>
          <li>share account credentials contrary to the purchased plan;</li>
          <li>use the tool for unlawful, abusive or fraudulent purposes.</li>
        </UL>

        <H3>4.3 Availability and changes</H3>
        <P>Plan Measure is an online service and may occasionally be unavailable because of maintenance, security updates, third-party service interruptions, technical faults or circumstances outside House Plan Direct&apos;s reasonable control.</P>
        <P>House Plan Direct may update, amend, suspend or withdraw features where reasonably necessary for maintenance, security, legal compliance, performance, product improvement or service development.</P>

        <H3>4.4 Support</H3>
        <P>Plan Measure support, documentation and help content are provided for guidance on current tool features.</P>
        <P>Support content does not constitute legal, surveying, valuation, planning, structural, Building Regulations, title-boundary or EPC-accreditation advice.</P>

        <H3>4.5 Plan Measure Outputs</H3>
        <P>Plan Measure Outputs are available in <strong>PNG, PDF and DXF formats only</strong>. Plan Measure Outputs do not include JPEG or DWG exports.</P>

        {/* ---- Section 5 ---- */}
        <H2 id="section-5">5. Source Material and plan rights</H2>
        <P>
          Customers are responsible for ensuring they have the right to upload, copy, trace, measure, adapt, convert, store and use
          all Source Material submitted through House Plan Direct or Plan Measure.
        </P>
        <P>
          By submitting Source Material, the Customer confirms that they created it, own the relevant rights in it, or have the
          required permission or licence to use it for the intended purpose.
        </P>
        <P>
          House Plan Direct does not verify ownership of every uploaded file and does not grant Customers any rights in third-party material.
        </P>
        <P>
          Ownership of a property or possession of a plan does not necessarily mean that a Customer owns copyright in the drawing.
        </P>
        <P>
          Where the Customer asks House Plan Direct to trace, redraw, reproduce, convert, adapt or otherwise use Source Material in
          providing a service, the Customer warrants that they have all rights, permissions and licences necessary to authorise
          House Plan Direct to carry out those activities on their behalf.
        </P>
        <P>
          House Plan Direct may restrict access to, suspend or remove Source Material or Outputs where it reasonably
          believes that the material may infringe another person&apos;s rights, breach these Terms, expose House Plan Direct or others to
          legal risk, or where action is required to comply with law.
        </P>
        <P>
          House Plan Direct may retain limited copies or records where reasonably necessary for legal, evidential, security,
          dispute-resolution or regulatory purposes.
        </P>
        <P>
          See the <Link to="/legal/copyright-and-takedown" className="text-blue-600 underline">Copyright &amp; Takedown Policy</Link> for the reporting process.
        </P>

        {/* ---- Section 6 ---- */}
        <H2 id="section-6">6. Intellectual property</H2>
        <H3>6.1 Customer material</H3>
        <P>The Customer retains any intellectual-property rights that it owns in Source Material.</P>
        <P>The Customer grants House Plan Direct a limited, non-exclusive licence to use, copy, reproduce, trace, adapt, process, host, display, export and store the Source Material solely to the extent reasonably necessary to provide the services requested by the Customer. This licence does not transfer ownership of the Source Material to House Plan Direct, is limited to performing the requested service, and does not grant House Plan Direct any right to exploit the Source Material commercially for its own purposes.</P>

        <H3>6.2 House Plan Direct intellectual property</H3>
        <P>House Plan Direct retains all rights in its websites, software, templates, tools, measurement interfaces, symbols, libraries, styles, branding, help content, workflow, underlying technology and other intellectual property.</P>

        <H3>6.3 Managed-Service Deliverables</H3>
        <P>Subject to these Terms and to any pre-existing third-party rights in the Source Material, once payment has been received in full, House Plan Direct grants the Customer a perpetual, non-exclusive, non-transferable licence to use the Managed-Service Deliverables for the relevant property&apos;s marketing, sale, letting, property-management, internal record and related professional purposes.</P>
        <P>House Plan Direct does not purport to grant the Customer any rights in third-party copyright that may exist within the Source Material, and cannot grant such rights merely because that material has been incorporated into Source Material supplied by the Customer.</P>
        <P>The Customer may share Managed-Service Deliverables with estate agents, landlords, tenants, purchasers, solicitors, surveyors and other relevant professionals for the relevant property.</P>

        <H3>6.4 Plan Measure Outputs</H3>
        <P>Subject to these Terms and any rights in third-party Source Material, House Plan Direct grants the Customer a perpetual, non-exclusive licence to use Plan Measure Outputs generated through their own account for the relevant property&apos;s marketing, sale, letting, property-management, internal record and related professional purposes.</P>

        <H3>6.5 Restrictions</H3>
        <P>Customers must not:</P>
        <UL>
          <li>sell, sublicense or distribute House Plan Direct software, templates, symbol libraries or tools as standalone products;</li>
          <li>remove House Plan Direct branding or notices where they are included in an output, unless the applicable paid plan expressly allows removal;</li>
          <li>use an output for a different property in a misleading way;</li>
          <li>claim ownership of House Plan Direct&apos;s software, templates or design system;</li>
          <li>make the platform or its proprietary content available to unauthorised third parties.</li>
        </UL>

        <H3>6.6 Portfolio use</H3>
        <P>House Plan Direct will use a Customer&apos;s completed plan, project image or property information for portfolio, marketing or promotional purposes only where the Customer has given separate permission or where the material has been fully anonymised and cannot reasonably identify a person, property or confidential information.</P>

        {/* ---- Section 7 ---- */}
        <H2 id="section-7">7. Pricing, payment and VAT</H2>
        <P>Prices are shown in pounds sterling.</P>
        <P>For the Managed Plan Service, payment is required before final JPEG, PDF and DWG files are released, unless House Plan Direct agrees otherwise in writing.</P>
        <P>For Plan Measure, the applicable subscription, credits, pay-per-export fee or other stated price is payable as shown before access or use, depending on the selected product.</P>
        <P>Where VAT is chargeable, it will be shown at checkout or on the invoice.</P>
        <P>Any optional extras, rush requests, custom formats or additional revisions will be quoted before work proceeds where practicable.</P>

        {/* ---- Section 8 ---- */}
        <H2 id="section-8">8. Consumer cancellation, digital access and refunds</H2>
        <div className="rounded-md bg-blue-50 border border-blue-200 p-4 text-sm text-blue-900 mb-3">
          <strong>Solicitor review note.</strong> Wording in this section needs specific UK consumer-law confirmation before the paid Plan Measure launch.
        </div>
        <H3>Consumer cancellation rights</H3>
        <P>Different cancellation rights can apply depending on whether you are purchasing a managed service, immediate digital access, a subscription, credits or digital outputs. Nothing in these Terms removes statutory rights that cannot lawfully be excluded.</P>

        <H3>Managed services</H3>
        <P>Where a Consumer asks House Plan Direct to begin the Managed Plan Service during an applicable cancellation period, the Consumer may be required to pay a proportionate amount for work performed before cancellation, where permitted by law.</P>

        <H3>Plan Measure</H3>
        <P>Where a Consumer is given immediate access to Plan Measure or other digital content, House Plan Direct will seek any consent and acknowledgement required by law before access begins.</P>

        <H3>Refunds generally</H3>
        <P>Refund requests will be considered in line with applicable consumer law, the nature of the product purchased, the work completed and the circumstances of the issue.</P>

        <H3>Checkout requirements</H3>
        <UL>
          <li>Terms, Refunds &amp; Cancellation Policy and Privacy Policy are displayed before payment.</li>
          <li>Affirmative acceptance of Terms is required before account creation or checkout.</li>
          <li>For immediate access to a paid digital tool, a separate unticked checkbox or equivalent affirmative action is required.</li>
          <li>Order confirmation, Terms version and relevant cancellation information will be sent by email or another durable medium.</li>
          <li>A record of acceptance and consent is kept.</li>
        </UL>

        {/* ---- Section 9 ---- */}
        <H2 id="section-9">9. Accuracy, verification and output disclaimers</H2>
        <P>Managed-Service Deliverables and Plan Measure Outputs are prepared from customer-supplied information, measurements, Source Material and/or user-selected tool settings.</P>
        <P>Dimensions, areas, room names, boundaries, labels, notes and layouts may be approximate and must be independently checked on site before reliance.</P>
        <P>Unless expressly marked as independently verified in writing, outputs are for illustrative, marketing, layout-understanding and preliminary measurement purposes only.</P>
        <P>Outputs are not surveys, title plans, legal boundary plans, valuations, construction drawings, planning drawings, structural drawings, Building Regulations drawings, safety drawings or professional certifications.</P>
        <P>Customers must not rely on outputs alone for legal, financial, construction, valuation, lease, title, boundary, planning, safety or regulatory decisions.</P>
        <P>Plan Measure is a tool to assist with drawing and measurement workflows. It does not replace a site inspection, professional survey, accredited assessment, professional judgement or independent verification.</P>

        {/* ---- Section 10 ---- */}
        <H2 id="section-10">10. Liability</H2>
        <P>Nothing in these Terms excludes or limits liability for death or personal injury caused by negligence, fraud or fraudulent misrepresentation, or any liability that cannot lawfully be excluded or limited.</P>
        <P><strong>Consumers.</strong> House Plan Direct will provide services with reasonable care and skill. Statutory consumer rights are not affected.</P>
        <P><strong>Business Customers.</strong> House Plan Direct is not liable for indirect or consequential loss, loss of profit, loss of business, loss of opportunity, loss of goodwill, loss of anticipated savings, business interruption or loss arising from reliance on unverified outputs, except where liability cannot lawfully be excluded.</P>
        <P>For Business Customers, House Plan Direct&apos;s total aggregate liability arising from a specific Managed Plan Service order will not exceed the amount paid for that order.</P>
        <P>For Business Customers using Plan Measure, House Plan Direct&apos;s total aggregate liability arising from use of Plan Measure will not exceed the amount paid by that Business Customer for Plan Measure during the 12 months immediately before the event giving rise to the claim.</P>

        {/* ---- Section 11 ---- */}
        <H2 id="section-11">11. Data protection and privacy</H2>
        <P>We process personal data in accordance with our <Link to="/legal/privacy" className="text-blue-600 underline">Privacy Policy</Link> and the lawful bases described there.</P>
        <P>Project data, uploaded plans, measurements, contact information, technical information and support requests may be processed to provide services, maintain security, investigate faults, comply with legal obligations and improve the service, as described in the Privacy Policy.</P>
        <P>House Plan Direct will not use a Customer&apos;s uploaded project content to train an AI model unless the Customer has expressly opted in.</P>

        {/* ---- Section 12 ---- */}
        <H2 id="section-12">12. Complaints, support and copyright reports</H2>
        <P>For service complaints, contact <a href={`mailto:${COMPANY.email}`} className="text-blue-600 underline">{COMPANY.email}</a> with your order or account reference, a description of the issue and the outcome you are seeking.</P>
        <P>For copyright concerns, use the <Link to="/legal/copyright-and-takedown" className="text-blue-600 underline">Copyright &amp; Takedown Policy</Link> and reporting process.</P>
        <P>We aim to acknowledge complaints within 2 Working Days and provide a substantive response within 10 Working Days. Complex matters may take longer, and we will keep the Customer informed where reasonably possible.</P>

        {/* ---- Section 13 ---- */}
        <H2 id="section-13">13. Suspension and termination</H2>
        <P>House Plan Direct may suspend, restrict or terminate a Customer&apos;s account, order or access where reasonably necessary because:</P>
        <UL>
          <li>the Customer breaches these Terms;</li>
          <li>payment is overdue;</li>
          <li>Source Material is suspected to infringe rights;</li>
          <li>there is fraud, unlawful activity, abuse or a security risk;</li>
          <li>the Customer interferes with the service or other users;</li>
          <li>continuing to provide the service would be unlawful or impracticable.</li>
        </UL>
        <P>Where House Plan Direct ends a service for reasons not caused by Customer breach, it will provide a fair refund where appropriate, subject to work already performed and applicable law.</P>

        {/* ---- Section 14 ---- */}
        <H2 id="section-14">14. Changes to these Terms</H2>
        <P>House Plan Direct may update these Terms from time to time.</P>
        <P>Minor updates may take effect when published.</P>
        <P>For material changes affecting paid customers, House Plan Direct will give reasonable advance notice where practicable.</P>
        <P>Changes will not retrospectively alter the terms applying to a Managed Plan Service order that has already been accepted, unless required by law or agreed with the Customer.</P>

        {/* ---- Section 15 ---- */}
        <H2 id="section-15">15. General</H2>
        <UL>
          <li><strong>Entire agreement.</strong> These Terms, together with any written order confirmation, form the entire agreement between the parties for the applicable service.</li>
          <li><strong>Severability.</strong> If any part of these Terms is found unenforceable, the remainder will continue to apply.</li>
          <li><strong>No waiver.</strong> Any delay in enforcing rights under these Terms is not a waiver of those rights.</li>
          <li><strong>Assignment.</strong> The Customer may not transfer their rights or obligations without House Plan Direct&apos;s written consent. House Plan Direct may assign or transfer its rights and obligations.</li>
          <li><strong>Third-party rights.</strong> A person who is not a party to these Terms has no rights under the Contracts (Rights of Third Parties) Act 1999 to enforce any term.</li>
          <li><strong>Force majeure.</strong> Neither party is liable for delay or failure to perform to the extent caused by events beyond its reasonable control.</li>
          <li><strong>Electronic communications.</strong> The parties agree to communicate electronically where practicable.</li>
          <li><strong>No partnership.</strong> Nothing in these Terms creates a partnership, agency or employment relationship between the parties.</li>
        </UL>

        {/* ---- Section 16 ---- */}
        <H2 id="section-16">16. Governing law</H2>
        <P>These Terms are governed by the law of England and Wales.</P>
        <P>For Business Customers, the courts of England and Wales will have exclusive jurisdiction.</P>
        <P>For Consumers, nothing in these Terms removes any mandatory protections or rights available under the law of the country in which the Consumer lives.</P>

        {/* ---- Section 17 ---- */}
        <H2 id="section-17">17. Contact details</H2>
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-5 text-sm text-gray-800 leading-relaxed" data-testid="terms-contact-block">
          <p><strong>House Plan Direct</strong></p>
          <p>A trading name of {COMPANY.legalName}</p>
          <p>Company number: {COMPANY.companyNumber}</p>
          <p>Registered in {COMPANY.registeredIn}</p>
          <p>Registered office: {COMPANY.registeredOffice}</p>
          <p>Correspondence address: {COMPANY.correspondenceAddress}</p>
          <p>Email: <a href={`mailto:${COMPANY.email}`} className="text-blue-600 underline">{COMPANY.email}</a></p>
          <p>Telephone: <a href={`tel:${COMPANY.telephone.replace(/\s+/g, '')}`} className="text-blue-600 underline">{COMPANY.telephone}</a></p>
        </div>

        {/* Back to top */}
        <div className="mt-10 no-print">
          <a href="#" className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1">
            <ExternalLink className="h-3.5 w-3.5" /> Back to top
          </a>
        </div>
      </main>

      <div className="no-print"><Footer /></div>
    </div>
  );
};

export default TermsAndConditions;
